import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
	ThumbsUp,
	ThumbsDown,
	ArrowLeft,
	ArrowRight,
	QLogo,
} from '@quizzach/assets';
import { Colors, KChip, Text, TextSize } from '@quizzach/styles';
import { FeedbackBottom, PlayNavButton } from '@quizzach/play-styles';
import { IQuestion } from '@quizzach/interfaces';
import { renderDifficulty } from '@quizzach/utils';
import {
	BottomNavOptions,
	ModalShell,
	ReportIssueModal,
} from '@quizzach/components';

const LEVEL_TONES: Record<number, 'teal' | 'gold' | 'coral'> = {
	1: 'teal',
	2: 'gold',
	3: 'coral',
};

interface IBottomNavProps {
	type: 'plain' | 'feedback' | 'button' | 'hidden';
	handlePrevious?: VoidFunction;
	handleNext?: VoidFunction;
	children?: JSX.Element;
	question?: IQuestion;
	setFeedback?: any;
	isPremadeOrEstimation?: boolean;
}
export const BottomNavMobile = ({
	type,
	handlePrevious,
	handleNext,
	question,
	setFeedback,
	isPremadeOrEstimation,
}: IBottomNavProps) => {
	const { t } = useTranslation();
	const [showModal, setShowModal] = useState<boolean>(false);
	const [showInfo, setShowInfo] = useState<boolean>(false);
	const [like, setLike] = useState<boolean | null>(null);
	const [difficulty, setDifficulty] = useState<number | null>(null);

	const handleLike = (type: 'like' | 'dislike') => {
		if (type === 'like') {
			setLike(like === true ? null : true);
		} else if (type === 'dislike') {
			setLike(like === false ? null : false);
		}
	};

	const handleDifficulty = (level: 1 | 2 | 3) => {
		setDifficulty(difficulty === level ? null : level);
	};

	useEffect(() => {
		if (type === 'feedback' && setFeedback) {
			setFeedback({ liked: like, level: difficulty });
		}
	}, [like, difficulty]);

	useEffect(() => {
		if (type === 'feedback') {
			setDifficulty(null);
			setLike(null);
		}
	}, [question]);

	const levelLabel = (level: 1 | 2 | 3) =>
		level === 1
			? t('common.feedback.easy')
			: level === 2
				? t('common.feedback.medium')
				: t('common.feedback.hard');

	return (
		<>
			<ReportIssueModal
				showModal={showModal}
				question_uuid={question?.uuid || ''}
				closeModal={() => setShowModal(false)}
			/>
			{/* the (?) opens the combined feedback explanation as a sheet */}
			<ModalShell
				show={showInfo}
				onClose={() => setShowInfo(false)}
				title={t('quiz.break.feedbackTitle')}
			>
				<Text color={Colors.textSoft} $size={TextSize.s}>
					{t('quiz.break.feedbackMessage')}
				</Text>
				<Text color={Colors.textMuted} $size={TextSize.xs}>
					{t('common.feedback.currentUserEstimate')}{' '}
					{renderDifficulty(question?.level || 0)}
				</Text>
			</ModalShell>
			<FeedbackBottom>
				{!isPremadeOrEstimation && type === 'feedback' && (
					<div className='feedback-row'>
						<div className='thumbs'>
							<div className='thumb' onClick={() => handleLike('like')}>
								<ThumbsUp
									color={like === true ? Colors.textStrong : Colors.textSoft}
									size={26}
								/>
							</div>
							<div className='thumb' onClick={() => handleLike('dislike')}>
								<ThumbsDown
									color={like === false ? Colors.textStrong : Colors.textSoft}
									size={26}
								/>
							</div>
						</div>
						<div className='level'>
							{([1, 2, 3] as const).map((level) => (
								<KChip
									key={level}
									type='button'
									$on={difficulty === level}
									$tone={LEVEL_TONES[level]}
									onClick={() => handleDifficulty(level)}
								>
									{levelLabel(level)}
								</KChip>
							))}
							<button
								type='button'
								className='qmark'
								aria-label={t('quiz.break.feedbackTitle')}
								onClick={() => setShowInfo(true)}
							>
								<QLogo size={10} />
							</button>
						</div>
					</div>
				)}

				<div className='fixed-container'>
					<div className='bottom-nav navigation'>
						<div style={{ flexBasis: '30%' }}></div>
						<div className='actions'>
							<PlayNavButton
								type='button'
								$small
								aria-label={t('quiz.options.previousSlide')}
								onClick={() => {
									if (navigator.vibrate) navigator.vibrate(30);
									handlePrevious?.();
								}}
							>
								<ArrowLeft size={20} />
							</PlayNavButton>

							<PlayNavButton
								type='button'
								$small
								className='pri'
								aria-label={t('quiz.options.nextSlide')}
								onClick={() => {
									if (navigator.vibrate) navigator.vibrate(30);
									handleNext?.();
								}}
							>
								<ArrowRight size={22} />
							</PlayNavButton>
						</div>

						<BottomNavOptions setShowModal={setShowModal} />
					</div>
				</div>
			</FeedbackBottom>
		</>
	);
};
